import React, { type MutableRefObject, useRef, useState } from 'react';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { type TaskConflict } from 'src/services/jmr/types';
import { useAppSelector } from 'src/store/store';
import { pluralizeString } from 'src/utils/helpers';
import {
  canJobBeModified,
  ConflictColorCode,
  createDataCyForJob,
  getNumModifiedConflicts,
  hasJobBeenModified,
  hasJobBeenModifiedForAllConflicts,
  isJobAtCapacity,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import {
  getConflictsType,
  getModifiedShiftLength,
  getRequirementColor,
  getRequirementQuantity,
} from 'src/utils/placementSelectionUtils';

interface ConflictTagProps {
  jobMatch: TableDataRow;
  exceededRestrictionIds: Set<number>;
}

const ConflictPopover = ({ jobMatch, exceededRestrictionIds }: ConflictTagProps) => {
  const buttonRef = useRef();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { shiftLength, limitedHoursPerDay } = useAppSelector((state) => state.newJmrPage.backgroundFormData);

  /** closure capturing exceededRestrictions for brevity in tsx attribute */
  const _getRequirementColor = (conflict: TaskConflict) => getRequirementColor(conflict, exceededRestrictionIds);

  const getConflictSummaryText = () => {
    const numConflicts = getNumModifiedConflicts(jobMatch.conflicts);
    const remainingConflicts = jobMatch?.conflicts.length - numConflicts;
    const conflictType = getConflictsType(jobMatch);

    if (hasJobBeenModified(jobMatch.conflicts)) {
      return (
        <Text>
          <strong>
            {numConflicts} job {pluralizeString('requirement', numConflicts)}
          </strong>{' '}
          {numConflicts === 1 ? 'has' : 'have'} been modified
          {hasJobBeenModifiedForAllConflicts(jobMatch.conflicts)
            ? ' to allow the associate to work the full shift.'
            : '.'}
        </Text>
      );
    }

    switch (conflictType) {
      case ConflictColorCode.IMPERFECT_CONFLICTS:
      case ConflictColorCode.NO_CONFLICTS:
        return jobMatch.perfectMatch ? (
          <Text>This job does not require any tasks the associate is restricted in.</Text>
        ) : (
          <Text>Based on restrictions the associate can perform this job for the full shift without any conflict.</Text>
        );
      case ConflictColorCode.SAFE_CONFLICTS:
      case ConflictColorCode.UNSAFE_CONFLICTS_BUT_MODIFICATIONS_AVAILABLE:
        return (
          <Text>
            Based on restrictions the maximum amount of time the associate can perform this job is:{' '}
            <strong>
              {jobMatch.maximumHoursAssociateCanDo} hrs. {remainingConflicts} job{' '}
              {pluralizeString('requirement', remainingConflicts)}
            </strong>{' '}
            must be modified to allow the associate to work the full shift
            {!canJobBeModified(jobMatch.conflicts, jobMatch.shifts, jobMatch.jobCapacity) ? (
              <>, but no modifications are present.</>
            ) : (
              <>.</>
            )}
          </Text>
        );
      case ConflictColorCode.UNSAFE_CONFLICTS_AND_NO_MODIFICATIONS_AVAILABLE:
        return (
          <Text>
            Based on restrictions the associate cannot perform this job and modifications are unavailable. Missing
            modification? Please submit a{' '}
            <Link type="secondary" target="_blank" href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL}>
              SIM ticket
            </Link>
            .
          </Text>
        );
      default:
        return null;
    }
  };

  return (
    <div
      onMouseLeave={() => setIsPopoverOpen(false)}
      onMouseEnter={() => setIsPopoverOpen(true)}
      style={{ zIndex: '1' }} // Keep this component's margin on top of the Maximum Hrs text for popover consistency
    >
      <ConflictTag jobMatch={jobMatch} buttonRef={buttonRef} />
      <Popover
        aria-label={`${jobMatch.name} Conflict Popover`}
        anchorNode={buttonRef.current}
        open={isPopoverOpen}
        onClose={() => setIsPopoverOpen(false)}
        position="top"
      >
        <PopoverHeader closeLabel="Close">Requirement Conflicts</PopoverHeader>

        <Column data-cy={`${createDataCyForJob(jobMatch.name)}-conflictDetails`}>
          {!isJobAtCapacity(jobMatch.shifts, jobMatch.jobCapacity) ? (
            <>
              {getConflictSummaryText()}
              <Column spacingInset="none" spacing="200">
                {jobMatch.conflicts?.map((conflict) => (
                  <Row spacing="200" key={conflict.taskId}>
                    <Text color={_getRequirementColor(conflict)} type="h100">
                      {conflict.taskName}
                    </Text>
                    {conflict.requirementProportion && (
                      <Text color={_getRequirementColor(conflict)}>
                        (Required for{' '}
                        {getRequirementQuantity(
                          jobMatch,
                          conflict,
                          getModifiedShiftLength(shiftLength, limitedHoursPerDay),
                        )}
                        )
                      </Text>
                    )}
                  </Row>
                ))}
              </Column>
            </>
          ) : (
            <Text>Job is at capacity.</Text>
          )}
        </Column>
      </Popover>
    </div>
  );
};

const ConflictTag = ({ jobMatch, buttonRef }: { jobMatch: TableDataRow; buttonRef: MutableRefObject<any> }) => {
  const remainingConflicts = jobMatch?.conflicts.length - getNumModifiedConflicts(jobMatch.conflicts);
  const isUsingT2Experience = useIsT2Experience();

  const getT1ConflictsString = () => {
    if (hasJobBeenModifiedForAllConflicts(jobMatch.conflicts)) {
      return 'Modified';
    }

    return `${remainingConflicts} ${pluralizeString('Conflict', remainingConflicts)} ${hasJobBeenModified(jobMatch.conflicts) ? 'Remaining' : ''}`;
  };

  const getT2ConflictsString = () => {
    if (jobMatch.tierTwoPlusJobModified) {
      return jobMatch.modificationNotes?.trim() ? 'Modified' : 'Conflicts';
    }

    return '0 Conflicts';
  };

  return (
    <div
      className="conflictPopover__tag"
      data-cy={`${createDataCyForJob(jobMatch.name)}-conflictTag`}
      aria-label={`${jobMatch.name} Conflict Tag`}
    >
      <Tag ref={buttonRef} type={getConflictsType(jobMatch)}>
        {isUsingT2Experience ? getT2ConflictsString() : getT1ConflictsString()}
      </Tag>
    </div>
  );
};

export default ConflictPopover;
